import React from 'react';
import { User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HotelFormSection, HotelField } from './HotelFormSection';
import { HotelFormActions } from './HotelFormActions';
import { useHotelForm } from '@/hooks/hotels/useHotelForm';
import { RoommateField } from './RoommateField';

interface HotelFormProps {
  editHotelId?: string;
  onEditComplete?: () => void;
  tripId: string; // Add tripId prop
}

export const HotelForm: React.FC<HotelFormProps> = ({ editHotelId, onEditComplete, tripId }) => {
  const { 
    formData, 
    handleFieldChange, 
    saveHotel, 
    isFormValid,
    addRoommate,
    removeRoommate,
    isEditMode,
    cancelEdit
  } = useHotelForm(editHotelId, tripId); // Pass tripId to the hook

  const handleSave = () => {
    saveHotel();
    if (onEditComplete) {
      onEditComplete();
    }
  };

  const handleCancel = () => {
    cancelEdit();
    if (onEditComplete) {
      onEditComplete();
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {isEditMode ? 'Edit Hotel Details' : 'Add Hotel Details'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <HotelFormSection>
            <HotelField 
              id="hotel-name"
              label="Hotel Name"
              value={formData.name}
              onChange={(value) => handleFieldChange('name', value)}
              placeholder="e.g., Grand Hotel"
            />

            <HotelField 
              id="hotel-added-by"
              label="Traveler Name (displayed to trip members)"
              value={formData.addedBy}
              onChange={(value) => handleFieldChange('addedBy', value)}
              placeholder="Your name"
              icon={<User className="h-4 w-4 text-muted-foreground" />}
            />

            <HotelField 
              id="hotel-address"
              label="Hotel Address"
              value={formData.address}
              onChange={(value) => handleFieldChange('address', value)}
              placeholder="e.g., 123 Main St, City, Country"
            />
          </HotelFormSection>

          <HotelFormSection title="Stay Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <HotelField 
                id="hotel-checkin"
                label="Check-in Date"
                type="date"
                value={formData.checkIn}
                onChange={(value) => handleFieldChange('checkIn', value)}
              />
              
              <HotelField 
                id="hotel-checkout"
                label="Check-out Date"
                type="date"
                value={formData.checkOut}
                onChange={(value) => handleFieldChange('checkOut', value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <HotelField 
                id="hotel-price"
                label="Price per Night"
                type="number"
                value={String(formData.price)} // Convert to string here
                onChange={(value) => handleFieldChange('price', value)}
                placeholder="e.g., 150"
              />
              
              <HotelField 
                id="hotel-phone"
                label="Phone Number"
                value={formData.phone}
                onChange={(value) => handleFieldChange('phone', value)}
                placeholder="e.g., +1 555-123-4567"
              />
            </div>
          </HotelFormSection>

          <HotelFormSection title="Additional Information">
            <RoommateField 
              roommates={formData.roommates}
              onAdd={addRoommate}
              onRemove={removeRoommate}
            />

            <HotelField 
              id="hotel-confirmation"
              label="Confirmation/Booking Number"
              value={formData.confirmation}
              onChange={(value) => handleFieldChange('confirmation', value)}
              placeholder="e.g., ABC123XYZ"
            />

            <HotelField 
              id="hotel-notes"
              label="Notes"
              value={formData.notes}
              onChange={(value) => handleFieldChange('notes', value)}
              placeholder="Additional information about your stay..."
              isTextarea={true}
            />
          </HotelFormSection>
          
          <HotelFormActions 
            onSave={handleSave} 
            onCancel={handleCancel}
            isValid={isFormValid} 
            isEditMode={isEditMode}
          />
        </div>
      </CardContent>
    </Card>
  );
};
